import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../axiosInstance';
import Color from 'color';
import {
  TextField,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Button,
  CircularProgress,
  Badge
} from '@mui/material';

const getHexFromColorName = (name) => {
  try {
    return Color(name).hex();
  } catch {
    return '#ccc';
  }
};

const BarcodeLookup = () => {
  const [barcode, setBarcode] = useState('');
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [error, setError] = useState('');
  const [colorFilter, setColorFilter] = useState('ALL');
  const [nameFilter, setNameFilter] = useState('');
  const [onlyWithQuantity, setOnlyWithQuantity] = useState(false);
  const [loading, setLoading] = useState(false);

  const inputRef = useRef(null);

  const handleFocus = () => {
    setTimeout(() => {
      inputRef.current.select();
    }, 0);
  };

  const handleKeyPress = async (e) => {
    if (e.key === 'Enter' && barcode.trim() !== '') {
      setLoading(true);
      try {
        const response = await axiosInstance.get(`/Glitter/V1/GetStock/${barcode}`);
        if (response.data.success && Array.isArray(response.data.data)) {
          const sorted = [...response.data.data].sort((a, b) =>
            b.serial.localeCompare(a.serial)
          );
          setItems(sorted);
          setError('');
        } else {
          setItems([]);
          setError('No results found.');
        }
      } catch (err) {
        console.error(err);
        setItems([]);
        setError('No data.');
      } finally {
        setLoading(false);
      }
    }
  };

  const getUniqueColors = () => {
    const colors = items.map((item) => item.color);
    return ['ALL', ...Array.from(new Set(colors))];
  };

  useEffect(() => {
    let filtered = [...items];

    if (colorFilter !== 'ALL') {
      filtered = filtered.filter((item) => item.color === colorFilter);
    }

    if (nameFilter.trim()) {
      filtered = filtered.filter((item) =>
        item.item.toLowerCase().includes(nameFilter.toLowerCase())
      );
    }

    if (onlyWithQuantity) {
      filtered = filtered.filter((item) => item.quantity > 0);
    }

    setFilteredItems(filtered);
  }, [items, colorFilter, nameFilter, onlyWithQuantity]);

  const resetFilters = () => {
    setColorFilter('ALL');
    setNameFilter('');
    setOnlyWithQuantity(false);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto', fontFamily: 'Cairo, sans-serif' }} dir="ltr">
      <TextField
        fullWidth
        label={<Typography fontWeight="bold">Enter Barcode</Typography>}
        variant="outlined"
        inputRef={inputRef}
        value={barcode}
        onChange={(e) => setBarcode(e.target.value)}
        onKeyPress={handleKeyPress}
        onFocus={handleFocus}
      />

      {loading && (
        <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={24} color="primary" />
          <Typography fontWeight="bold">Searching...</Typography>
        </Box>
      )}

      {!loading && items.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 3, alignItems: 'center' }}>
          <TextField
            select
            label={<Typography fontWeight="bold">Color</Typography>}
            value={colorFilter}
            onChange={(e) => setColorFilter(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            {getUniqueColors().map((color) => (
              <MenuItem key={color} value={color}>
                {color !== 'ALL' && (
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      backgroundColor: getHexFromColorName(color),
                      borderRadius: '50%',
                      display: 'inline-block',
                      mr: 1,
                      border: '1px solid #aaa',
                    }}
                  />
                )}
                {color}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label={<Typography fontWeight="bold">Item Name</Typography>}
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            sx={{ minWidth: 200 }}
            onFocus={(e) => e.target.select()}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={onlyWithQuantity}
                onChange={(e) => setOnlyWithQuantity(e.target.checked)}
              />
            }
            label={<Typography fontWeight="bold">Only items with quantity</Typography>}
          />

          <Button variant="outlined" color="secondary" onClick={resetFilters}>
            Reset Filters
          </Button>
        </Box>
      )}

      {!loading && error && (
        <Typography color="error" mt={2} fontWeight="bold">
          {error}
        </Typography>
      )}

      {!loading && filteredItems.length > 0 && (
        <>
          <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" fontWeight="bold">
              Results:
            </Typography>
            <Badge badgeContent={filteredItems.length} color="primary" />
          </Box>

          <Grid container spacing={3} mt={1}>
            {filteredItems.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    borderLeft: '8px solid',
                    borderColor:
                      item.quantity < 0
                        ? 'error.main'
                        : item.quantity > 0
                        ? 'success.main'
                        : 'black',
                    boxShadow: 4,
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" fontWeight="bold">
                      {item.item}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      <b>Barcode:</b> {item.barcode}
                    </Typography>

                    <Typography variant="body2">
                      <b>Color:</b> {item.color}
                    </Typography>

                    <Box
                      sx={{
                        height: 16,
                        width: 60,
                        background: getHexFromColorName(item.color.toLowerCase()),
                        borderRadius: 1,
                        border: '1px solid #999',
                        mt: 0.5,
                        mb: 1,
                      }}
                    />

                    <Typography variant="caption" color="text.secondary">
                      <b>Serial:</b> {item.serial}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color:
                          item.quantity < 0
                            ? 'error.main'
                            : item.quantity > 0
                            ? 'success.main'
                            : 'text.primary',
                      }}
                    >
                      <b>Quantity:</b> {item.quantity}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {!loading && items.length > 0 && filteredItems.length === 0 && (
        <Typography mt={3} color="text.secondary" fontWeight="bold">
          No results match your filters.
        </Typography>
      )}
    </Box>
  );
};

export default BarcodeLookup;
